import { instance } from './index';
import {
  insertMachineData,
  machineCoinHistoryResponseData,
  modifyMachineCoinData,
  modifyMachineData,
  OrderOption,
  modifyMachineSortOrderData,
} from '@dataTypes/perMachineTypes';

/** 단말기 재고 API 응답 (GET /api/stock) */
export interface StockResponseItem {
  catId: string;
  terminalName?: string | null;
  stock: number | null;
  lastUpdated?: string | null;
}

/** WebSocket 단말기 상태 (GET /api/websocket/terminals) - usage_status, 연결 여부 등 */
export interface WebSocketTerminalItem {
  catId: string;
  businessNum?: string | null;
  terminalName?: string | null;
  /** 00=사용가능, 01=사용불가(점검중), 02=현금만 사용가능 */
  usageStatus?: string | null;
  /** 정보알림(C0) Version 필드 (예: V0001) */
  version?: string | null;
  /** C2(03) 전송으로 서버에 기록된 FW 자동업데이트: 01=사용, 00=사용안함, null=미기록 */
  fwAutoUpdate?: string | null;
  /** C2(06) 전송으로 서버에 기록된 인터넷 망 기능: 01=On, 00=Off, null=미기록 */
  internetNetwork?: string | null;
  /** C2(10) 서버 기록 밝기 1~10, null=미기록 */
  displayBrightness?: number | null;
  lastHeartbeat?: string | null;
  isConnected?: boolean;
  /** 동일 catId WebSocket 세션 수 (2 이상이면 공유) */
  connectedSessionCount?: number | null;
  stock?: number | null;
  stockManagementOn?: boolean | null;
  stockLastUpdated?: string | null;
  /** C0 접속 알림 Amount (설정 금액) */
  amount?: string | null;
  /** C0 접속 알림 Pulse */
  pulse?: string | null;
}

export const getPerMachineLists = async (storeId: number | undefined) => {
  const response = await instance.get(`/user/device/${storeId}`);
  return response.data;
};

/**
 * @description 단말기 재고 조회 (해당 매장만: storeId 전달 시, 전체: 미전달 시)
 * GET /api/stock?storeId=123 → 해당 매장 단말만 (마스터 계정에서 빠른 조회)
 * GET /api/stock → 전체 (storeId 미전달)
 */
export const getStockAll = async (storeId?: number | undefined): Promise<StockResponseItem[]> => {
  try {
    const params = storeId != null ? { storeId } : undefined;
    const { data } = await instance.get<StockResponseItem[]>('/stock', {
      params,
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

export const insertMachine = async (data: insertMachineData) => {
  const response = await instance.post(`/user/device/enrollment`, data);
  return response.data;
};

export const bulkInsertMachineByMaster = async (data: insertMachineData[]) => {
  const response = await instance.post(`/master/device/bulk-enroll`, data);
  return response.data;
};

export const getPerMachineTotalSale = async (
  storeId: number | undefined,
  date: string,
  order?: OrderOption,
) => {
  if (storeId === undefined) {
    return;
  }

  const params = new URLSearchParams();
  params.set('store-id', String(storeId));
  params.set('yyyymmdd', date);

  // order가 있으면 'amount-order' | 'alias-order' | 'cnt-order' 중 하나만 붙임
  if (order) {
    params.set(`${order.type}-order`, order.dir);
  }

  const response = await instance.get(`/user/sales/devices?${params.toString()}`);
  return response.data;
};

export const getPerMachineSummarySale = async (
  storeId: number | undefined,
  startDate: string,
  endDate: string,
  order?: OrderOption,
) => {
  if (storeId === undefined) {
    return;
  }

  const params = new URLSearchParams();
  params.set('store-id', String(storeId));
  params.set('start-date', startDate);
  params.set('end-date', endDate);

  // order가 있으면 'amount-order' | 'alias-order' | 'cnt-order' 중 하나만 붙임
  if (order) {
    params.set(`${order.type}-order`, order.dir);
  }

  const response = await instance.get(`/user/sales/devices/summary?${params.toString()}`);
  return response.data;
};

export const modifyMachine = async (data: modifyMachineData) => {
  const response = await instance.patch(`/user/device/update-alias`, data);
  return response.data;
};

/**
 * @description 단말기 코인 수정
 * @param catId: string 단말기 번호(8자리 제약, 필수)
 * @param delta:number 변화량(-255~255까지 제약, 필수)
 * @param idempotencyKey:string 멱등성키(15자리 제약, 필수)
 * @returns boolean
 */
export const modifyMachineCoin = async (data: modifyMachineCoinData): Promise<boolean> => {
  const response = await instance.put(`/user/service-coin/adjust`, data);
  return response.data;
};

/**
 * @description 단말기 코인 변동 내역 조회
 * @param catId: string 단말기 번호(8자리 제약, 필수)
 * @param page: number 페이지 번호(기본값: 1) pageSize:10
 * @returns type: (DEVICE_REQUEST: 단말기 요청, ADMIN_REQUEST: 관리자 서비스 코인 증감)
 * @returns currentCoin: 서비스 코인 당시 개수
 * @returns coinDelta: 서비스 코인 변화량
 * @returns createdDate: 발생일시
 */
export const machineCoinHistory = async (
  catId: string,
  page: number,
): Promise<machineCoinHistoryResponseData> => {
  const response = await instance.get(`/user/service-coin/history?cat-id=${catId}&page=${page}`);
  return response.data;
};

/**
 * @description 서비스 금액 WebSocket(S0) 지급 — S0 항목 ServiceCoin은 금액(원, 정수)
 * POST /api/websocket/service-coin/delivery (form-urlencoded)
 * @param catId: 단말기 ID
 * @param businessNum: 사업자 번호 (필수)
 * @param serviceCoin: 지급 금액(원)
 */
export const deliverServiceCoinBySocket = async (
  catId: string,
  businessNum: string,
  serviceCoin: number,
): Promise<{ success: boolean; message: string }> => {
  const params = new URLSearchParams();
  params.set('catId', catId);
  params.set('businessNum', businessNum);
  params.set('serviceCoin', String(serviceCoin));
  const { data } = await instance.post<{ success: boolean; message: string }>(
    '/websocket/service-coin/delivery',
    params,
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  return data;
};

/**
 * @description WebSocket 단말기 목록 조회 (연결 상태, usage_status 등)
 * GET /api/websocket/terminals?storeId=123 → 해당 매장 단말만
 * GET /api/websocket/terminals → 전체
 */
export const getWebSocketTerminals = async (
  storeId?: number | undefined,
): Promise<WebSocketTerminalItem[]> => {
  try {
    const params = storeId != null ? { storeId } : undefined;
    const { data } = await instance.get<WebSocketTerminalItem[]>('/websocket/terminals', {
      params,
    });
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/**
 * @description 원격제어 (C2) - 재부팅/사용설정/재고설정/FW자동업데이트/FW업데이트
 * POST /api/websocket/remote-control (form-urlencoded)
 * @param catId: 단말기 ID
 * @param businessNum: 사업자 번호
 * @param control: "00"=재부팅, "01"=단말기 사용설정, "02"=재고설정, "03"=FW자동업데이트 설정, "04"=FW업데이트, "05"=TMS 로그 전송(트리거), "06"=인터넷 망 기능, "10"=밝기
 * @param data: 01·03·06 규칙은 스펙 참고, 02는 "1"~"99"|"-", 10은 "1"~"10" — 00·04·05는 생략
 */
export const sendRemoteControl = async (
  catId: string,
  businessNum: string,
  control: string,
  data?: string | null,
): Promise<{ success: boolean; message: string }> => {
  const params = new URLSearchParams();
  params.set('catId', catId);
  params.set('businessNum', businessNum);
  params.set('control', control);
  if (data != null && data !== '') {
    params.set('data', data);
  }
  const { data: res } = await instance.post<{ success: boolean; message: string }>(
    '/websocket/remote-control',
    params,
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      /** 모바일 WebView 등에서 단건이 무한 대기하면 일괄 진행이 멈춘 것처럼 보이는 것 방지 */
      timeout: 90_000,
    },
  );
  return res;
};

/**
 * @description 단말기 순서 변경
 * @param storeId: 지점 번호
 * @param data: modifyMachineSortOrderData 단말기 순서 변경 데이터
 */
export const modifyMachineSortOrder = async (storeId: number, data: modifyMachineSortOrderData) => {
  const response = await instance.put(`/user/${storeId}/devices/sort-orders`, data);
  return response.data;
};
